<template>
  <div>
    <div class="pb-6 relative">
      <!-- MAIN PAGE -->
      <div class="center relative">
        <transition name="fade" mode="out-in">
          <div v-if="!domain" class="mb-12 mt-8">
            <div class="text-center">
              <h1 class="text-3xl font-bold mb-4 ot-anim">
                {{ $t("dashboard.title") }}
              </h1>
              <p class="text-gray-500 ot-anim ot-line mx-auto">
                <span class="">{{ $t(`dashboard.${dataset}Subtitle`) }}</span>
              </p>
            </div>

            <p
              class="text-center text-gray-500 text-sm mb-4 mt-8 ot-anim ot-line mx-auto"
            >
              <font-awesome-icon :icon="['far', 'hand-pointer']" class="mr-1" />
              {{ $t("dashboard.clickSubtitle") }}
            </p>

            <DomainGrid :domains="selectedDomains" :iso="iso" :small="!!domain" />
          </div>
        </transition>
      </div>

      <!-- DOMAIN PAGE -->
      <transition @enter="initial">
        <div v-if="domain">
          <!-- TOP SECTION -->
          <section class="bg-tertiary ot-section">
            <div class="center">
              <div class="md:w-3/5">
                <div class="text-white uppercase flex items-center">
                  {{ $t("domain") }}

                  <SelectCustom
                    :value="domainId"
                    @input="routeDomain"
                    :options="domainsOptions"
                    class="inline-block lg:hidden ml-2"
                    select-class="border-tertiary hover:bg-tertiary hover:bg-white focus:ring-tertiary"
                  />
                </div>
                <h1 class="text-3xl mb-4 ot-anim font-title tracking-tight">
                  {{ $t(domain.name) }}
                </h1>

                <div class="flex mb-4 max-w-xl">
                  <div class="ot-anim ot-line">
                    {{ $t(domain.text) }}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- INDICATOR TITLE -->
          <section class="ot-section">
            <div class="center">
              <div class="mb-4 ot-anim">
                <label for="indicator" class="mr-2">{{
                  $t("globalInsightsPage.select")
                }}</label>

                <SelectCustom
                  :value="indicatorId"
                  @input="routeIndicator"
                  :options="indicators"
                  :hasTranslation="true"
                  label="indicator"
                  class="w-full sm:w-auto max-w-sm"
                />
              </div>
              <h2
                class="text-3xl font-bold mb-2 ot-anim ot-line font-title tracking-tight"
              >
                {{ $t(indicator.name) }}

                <button
                  class="ml-2 text-primary hover:text-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary rounded-full border-0"
                  aria-label="More info"
                  @click="showModal = true"
                >
                  <font-awesome-icon :icon="['fas', 'info-circle']" class="" />
                </button>
              </h2>
              <p class="text-gray-500 ot-anim ot-line">
                {{ $t(indicator.text) }}
              </p>
            </div>
          </section>

          <!-- GLOBAL INSIGHTS SECTION -->
          <section class="bg-white ot-section">
            <div class="center">
              <div class="ot-card-title ot-anim">
                <h2
                  class="text-3xl ot-line ot-anim font-title tracking-tighter"
                >
                  {{ $t("globalInsightsPage.subtitle") }}
                </h2>
              </div>

              <div class="ot-card ot-anim">
                <div
                  class="flex flex-col sm:flex-row items-center justify-between mb-6"
                >
                  <h3
                    class="ot-line text-lg leading-tight tracking-tight mb-4 sm:mb-0"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'globe']"
                      class="mr-1 text-primary"
                    />
                    {{ $t("globalInsightsPage.name") }}
                  </h3>
                  <DownloadButton :href="globalDataLink" />
                </div>

                <div
                  class="ot-anim flex flex-col md:flex-row md:space-x-4 space-y-4 md:space-y-0"
                >
                  <div class="w-full md:w-2/5 flex flex-col justify-between">
                    <p class="ot-line text-sm mb-4">
                      {{ $t(indicator.name) }}: {{ $t(indicator.text) }}
                    </p>
                    <!-- COLUMN CHART -->
                    <div class="">
                      <h4 class="mb-4 font-medium font-title tracking-tight">
                        {{ $t("globalInsightsPage.ranked") }}
                      </h4>

                      <ColumnChart
                        :data="columnChartData"
                        :show-x-axis="false"
                        :show-y-axis="true"
                        :margin="{ top: 0, right: 30, bottom: 0, left: 0 }"
                        class="h-32 md:h-24 lg:h-36 xl:h-48 2xl:h-60 w-full"
                        :duration="500"
                        :selected="highlightedCountry"
                        @hover="
                          $event
                            ? showColumnTooltip($event)
                            : hideColumnTooltip()
                        "
                        @click="routeCountry($event.data.id)"
                      />
                      <!-- COLUMN CHART TOOLTIP -->
                      <VTooltip
                        :show="columnTooltip.show"
                        :target="columnTooltip.target"
                        class="z-30 w-80"
                      >
                        <div class="flex items-start">
                          <CountryFlag
                            :iso="columnTooltip.iso"
                            class="w-7"
                            v-if="columnTooltip.iso"
                          />
                          <div class="ml-4 overflow-x-hidden space-y-2">
                            <div class="font-bold text-base mt-0.5">
                              {{ columnTooltip.title }}
                            </div>
                            <div class="">{{ $t(columnTooltip.subtitle) }}</div>
                            <hr class="my-2" />
                            <div>
                              <div class="text-gray-500">
                                {{ $t("globalInsightsPage.percentOf") }}s
                              </div>
                              <div class="font-bold text-lg">
                                {{ columnTooltip.value }}%
                              </div>
                              <div class="">
                                {{ columnTooltip.number | number(0) }} of
                                {{ columnTooltip.total | number(0) }}
                              </div>
                            </div>

                            <div>
                              <div class="text-gray-500">
                                {{
                                  $t("globalInsightsPage.confidenceInterval")
                                }}
                              </div>
                              {{ columnTooltip.min }}% -
                              {{ columnTooltip.max }}%
                            </div>
                          </div>
                        </div>
                      </VTooltip>

                      <!-- FLAG TOOLTIP -->
                      <VTooltip
                        :show="flagTooltip.show"
                        :target="flagTooltip.target"
                        rounded
                        class="z-30"
                      >
                        <div class="flex items-start">
                          <CountryFlag
                            :iso="flagTooltip.iso"
                            class="w-7"
                            v-if="flagTooltip.iso"
                          />
                        </div>
                      </VTooltip>
                    </div>
                  </div>
                  <!-- WORLD MAP -->
                  <div class="w-full md:w-3/5 relative">
                    <!-- <div class="flex justify-end mb-3">
                      <DownloadButton :href="globalDataLink" />
                    </div> -->
                    <div class="">
                      <div class="relative">
                        <WorldMap
                          ref="worldMap"
                          :data="mapData"
                          :colors="globalColorScale"
                          :no-data-color="noColorData"
                          :stroke-color="mapStrokeColor"
                          :fitBoundsToData="fitBoundsToData"
                          :adjust-size="true"
                          class="h-48 sm:h-72 xl:h-80 2xl:h-96"
                          :selected="highlightedCountry"
                          @hover="
                            $event
                              ? highlightWorldMap($event)
                              : highlightWorldMap()
                          "
                          @click="routeMap"
                          :scale="mapZoom"
                          @zoom="setCurrentZoom"
                        />

                        <!-- WORLDMAP TOOLTIP -->
                        <VTooltip
                          :show="worldMapTooltip.show"
                          :followMouse="true"
                          class="z-30 w-80"
                        >
                          <div class="flex items-start">
                            <CountryFlag
                              :iso="worldMapTooltip.iso"
                              class="w-7"
                              v-if="worldMapTooltip.iso"
                            />
                            <div class="ml-4 overflow-x-hidden space-y-2">
                              <div class="font-bold text-base mt-0.5">
                                {{ worldMapTooltip.title }}
                              </div>
                              <div class="">
                                {{ $t(worldMapTooltip.subtitle) }}
                              </div>
                              <hr class="my-2" />
                              <div>
                                <div class="text-gray-500">
                                  {{ $t("globalInsightsPage.percentOf") }}s
                                </div>
                                <div class="font-bold text-lg">
                                  {{ worldMapTooltip.value }}%
                                </div>
                                <div class="">
                                  {{ worldMapTooltip.number | number(0) }} of
                                  {{ worldMapTooltip.total | number(0) }}
                                </div>
                              </div>

                              <div>
                                <div class="text-gray-500">
                                  {{
                                    $t("globalInsightsPage.confidenceInterval")
                                  }}
                                </div>
                                {{ worldMapTooltip.min }}% -
                                {{ worldMapTooltip.max }}%
                              </div>
                            </div>
                          </div>
                        </VTooltip>

                        <!-- MAP BUTTONS -->
                        <div class="absolute top-1 right-1 flex flex-col">
                          <button
                            type="button"
                            class="ot-btn p-1 h-6 w-6 rounded-b-none border border-gray-300 bg-white bg-opacity-80"
                            @click="increaseZoom"
                          >
                            <span class="sr-only">Zoom in</span>
                            <font-awesome-icon
                              :icon="['fas', 'plus']"
                              class=""
                            />
                          </button>
                          <button
                            type="button"
                            class="ot-btn -mt-px p-1 h-6 w-6 rounded-t-none border border-gray-300 bg-white bg-opacity-80"
                            @click="mapZoom--"
                          >
                            <span class="sr-only">Zoom out</span>
                            <font-awesome-icon
                              :icon="['fas', 'minus']"
                              class=""
                            />
                          </button>

                          <button
                            class="ot-btn p-1 h-6 w-6 mt-1 border border-gray-300 bg-white bg-opacity-80"
                            @click="zoomMap"
                          >
                            <font-awesome-icon
                              :icon="['fas', 'expand']"
                              class=""
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="flex justify-end pr-4 pt-2">
                  <div>
                    <LegendColor
                      :color="globalColorScale"
                      :tick-format="(d) => `${d}%`"
                      class="w-60"
                    />
                    <div class="text-xs mt-1 text-center">
                      {{ $t("globalInsightsPage.percentOf") }}
                      {{ $t(dataset) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- SUBNATIONAL BOUNDARIES SECTION -->
          <section class="bg-white ot-section pt-0">
            <div class="center">
              <div class="ot-anim">
                <div class="ot-card-title ot-anim z-20">
                  <!-- this is the tooltip of the Country map. We put it here because of the stacking contexts, otherwise it shows beneath the card title -->
                  <VTooltip
                    :show="countryMapTooltip.show"
                    :followMouse="true"
                    class="z-50 w-72"
                  >
                    <div class="space-y-2">
                      <div>
                        <div class="text-gray-500">Subnational region</div>
                        <strong class="text-base">{{
                          countryMapTooltip.name
                        }}</strong>
                      </div>
                      <div>
                        <div class="text-gray-500">Indicator</div>
                        {{ $t(countryMapTooltip.subtitle) }}
                      </div>
                      <hr class="my-2" />
                      <div>
                        <div class="text-gray-500">
                          {{ $t("globalInsightsPage.percentOf") }}
                        </div>

                        <strong class="text-lg"
                          >{{ countryMapTooltip.value }}%</strong
                        >
                        <div class="">
                          {{ countryMapTooltip.number | number(0) }} of
                          {{ countryMapTooltip.total | number(0) }}
                        </div>
                      </div>
                      <div>
                        <div class="text-gray-500">
                          {{ $t("globalInsightsPage.confidenceInterval") }}
                        </div>
                        {{ countryMapTooltip.min }}% -
                        {{ countryMapTooltip.max }}%
                      </div>
                    </div>
                  </VTooltip>

                  <!-- this is the tooltip of the Region Chart -->
                  <VTooltip
                    :show="regionChartTooltip.show"
                    :target="regionChartTooltip.target"
                    class="z-50 w-72"
                  >
                    <div class="space-y-2">
                      <div>
                        <div class="text-gray-500">Subnational region</div>
                        <strong class="text-base">{{
                          regionChartTooltip.name
                        }}</strong>
                      </div>
                      <div>
                        <div class="text-gray-500">Indicator</div>
                        {{ $t(regionChartTooltip.subtitle) }}
                      </div>
                      <hr class="my-2" />
                      <div>
                        <div class="text-gray-500">
                          {{ $t("globalInsightsPage.percentOf") }}s
                        </div>

                        <strong class="text-lg"
                          >{{ regionChartTooltip.value }}%</strong
                        >
                        <div class="">
                          {{ regionChartTooltip.number | number(0) }} of
                          {{ regionChartTooltip.total | number(0) }}
                        </div>
                      </div>
                      <div>
                        <div class="text-gray-500">
                          {{ $t("globalInsightsPage.confidenceInterval") }}
                        </div>
                        {{ regionChartTooltip.min }}% -
                        {{ regionChartTooltip.max }}%
                      </div>
                    </div>
                  </VTooltip>

                  <!-- tooltip for the "max" selector -->
                  <VTooltip
                    :show="maxTooltip.show"
                    :target="maxTooltip.target"
                    class="z-50 w-72"
                  >
                    <div>
                      {{ $t("globalInsightsPage.maxTooltip") }}
                    </div>
                  </VTooltip>

                  <h3 class="ot-line text-lg ot-anim tracking-tight">
                    <SearchSelect
                      :value="iso"
                      @input="routeCountry"
                      :options="countries"
                      class="inline-flex"
                      main-class="rounded hover:bg-gray-200 focus:bg-gray-200 ot-ring border-0 tracking-tight p-1"
                      placeholder="Type to search country"
                      default-option-name="---"
                      flag-class=""
                    />
                    <span class="ml-1">{{
                      $t("globalInsightsPage.subnational")
                    }}</span>
                  </h3>
                </div>

                <div class="ot-card ot-anim">
                  <div class="flex justify-center mb-8 bg-fifth rounded-2xl">
                    <div class="ot-line text-sm p-6">
                      <h4 class="font-bold mb-4 text-center">
                        {{ $t("globalInsightsPage.subnationalSummary") }}
                      </h4>
                      <p>{{ prevalenceText }}</p>
                    </div>
                  </div>

                  <div
                    class="flex flex-col sm:flex-row items-center justify-end"
                  >
                    <DownloadButton :href="countryDataLink" />
                  </div>

                  <div class="ot-anim grid gap-4 grid-cols-1 md:grid-cols-2">
                    <!-- REGION CHART -->
                    <div class="md:h-[28rem] flex flex-col">
                      <div
                        class="text-gray-500 text-xs text-center ml-28 flex justify-between"
                      >
                        <div>{{ $t("globalInsightsPage.percentOf") }}s</div>
                        <div
                          @mouseenter="showMaxTooltip"
                          @mouseleave="hideMaxTooltip"
                        >
                          Max:
                          <select v-model="maxCountryChart" class="border-0">
                            <option :value="null">Auto</option>
                            <option :value="100">100%</option>
                          </select>
                        </div>
                      </div>
                      <XAxis
                        v-if="dataAvailable"
                        :domain="xAxisDomain"
                        :tick-format="(d) => `${d}%`"
                        class="mt-3 ml-28 h-2 text-gray-500 z-10"
                      />
                      <div v-else class="mt-3 ml-28 h-2"></div>
                      <div
                        v-if="dataAvailable"
                        class="relative md:overflow-scroll md:h-96 md:flex-grow border-t border-b z-10 hide-scrollbar"
                      >
                        <RegionChart
                          :data="regionChartData"
                          :selected="
                            selectedRegion ? `${iso}-${selectedRegion}` : null
                          "
                          :color="middleColor"
                          :margin="{ top: 0, right: 0, bottom: 0, left: 112 }"
                          :show-x-axis="false"
                          :average="countryAverage"
                          :range="xAxisDomain"
                          class="text-gray-900"
                          @hover="setSelectedRegion"
                        />
                      </div>
                      <div v-else class="md:h-96 md:flex-grow ot-placeholder">
                        {{ $t("policyPage.noData") }}
                      </div>
                      <div
                        class="flex justify-between mt-2 mb-2 text-xs text-gray-500"
                      >
                        <div
                          class="border-l border-gray-700 border-dashed pl-2"
                        >
                          {{ countryName }}
                          {{ $t("globalInsightsPage.nationalAverage") }}
                        </div>
                        <div
                          class="text-right hidden md:block"
                          :class="{ 'opacity-0': regionChartData.length < 15 }"
                        >
                          <font-awesome-icon
                            :icon="['fas', 'arrows-alt-v']"
                            class="mr-1"
                          />
                          Scroll to see the all the regions in {{ countryName }}
                        </div>
                      </div>
                      <div class="text-xs text-gray-500 my-2">
                        {{ $t("globalInsightsPage.regionChartNote") }}
                      </div>
                    </div>
                    <!-- COUNTRY MAP -->
                    <div class="flex flex-col h-96 md:pt-5">
                      <div class="flex-grow relative md:mt-4">
                        <CountryMap
                          v-if="dataAvailable"
                          :iso="iso"
                          :data="countryMapData"
                          :adjust-size="true"
                          :accessor="accessorForCurrentCountry"
                          :colors="countryColorScale"
                          class="w-full h-full"
                          :selected="selectedRegion"
                          @hover="
                            $event
                              ? showCountryMapTooltip($event)
                              : hideCountryMapTooltip()
                          "
                          :scale="countryMapZoom"
                          @zoom="setCurrentCountryZoom"
                        />
                        <div v-else class="w-full h-full ot-placeholder">
                          {{ $t("policyPage.noData") }}
                        </div>
                      </div>

                      <div class="flex justify-end mt-2">
                        <div>
                          <LegendColor
                            v-if="dataAvailable"
                            :color="countryColorScale"
                            :tick-format="(d) => `${d}%`"
                            class="w-60"
                          />
                          <div class="text-xs mt-1 text-center">
                            {{ $t("globalInsightsPage.percentOf") }}
                            {{ dataset }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <h2
                    class="text-3xl ot-line ot-anim font-title tracking-tighter relative z-20 mb-4 mt-20"
                  >
                    {{ $t("globalInsightsPage.subtitle2") }}
                    <br />
                    <SearchSelect
                      :value="iso"
                      @input="routeCountry"
                      :options="countries"
                      class="inline-flex"
                      main-class="rounded hover:bg-gray-200 focus:bg-gray-200 ot-ring p-1 border-0 tracking-tighter"
                      placeholder="Type to search country"
                      default-option-name="---"
                      flag-class="top-2"
                    />?
                  </h2>

                  <!-- LINKED RISKS TABLE -->
                  <div
                    class="flex flex-col md:flex-row justify-between mb-4 space-y-4 md:space-y-0"
                  >
                    <h3 class="mb-2 ot-line">
                      {{ $t("globalInsightsPage.preTableText") }}
                      <strong>{{ $t(indicator.name) }}</strong>
                    </h3>
                    <div class="self-end md:self-start">
                      <DownloadButton :href="linkedRisksDataLink" />
                    </div>
                  </div>

                  <div
                    v-if="linkedRisksAvailable"
                    class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 md:divide-x my-6"
                  >
                    <div class="p-4">
                      <h4
                        class="font-bold uppercase mb-2"
                        :style="{ color: heatmapColorScale(0) }"
                      >
                        {{ $t("globalInsightsPage.notLinked") }}
                      </h4>
                      <ul class="list-disc list-outside text-sm ml-4 space-y-2">
                        <li
                          v-for="indicator in linkedRisksTable.no"
                          :key="indicator.name"
                        >
                          {{ $t(getIndicatorName(indicator.name)) }}
                        </li>
                      </ul>
                      <div
                        v-if="!linkedRisksTable.no.length"
                        class="text-gray-500 text-sm"
                      >
                        {{ $t("globalInsightsPage.noIndicators") }}
                      </div>
                    </div>
                    <div class="p-4">
                      <h4
                        class="font-bold uppercase mb-2"
                        :style="{
                          color: heatmapColorScale(heatmapThresholdValues[0]),
                        }"
                      >
                        {{ $t("globalInsightsPage.lowLinkage") }}
                      </h4>
                      <ul class="list-disc list-outside text-sm ml-4 space-y-2">
                        <li
                          v-for="indicator in linkedRisksTable.low"
                          :key="indicator.name"
                        >
                          {{ $t(getIndicatorName(indicator.name)) }}
                        </li>
                      </ul>
                      <div
                        v-if="!linkedRisksTable.low.length"
                        class="text-gray-500 text-sm"
                      >
                        {{ $t("globalInsightsPage.noIndicators") }}
                      </div>
                    </div>
                    <div class="p-4">
                      <h4
                        class="font-bold uppercase mb-2"
                        :style="{
                          color: heatmapColorScale(heatmapThresholdValues[1]),
                        }"
                      >
                        {{ $t("globalInsightsPage.someLinkage") }}
                      </h4>
                      <ul class="list-disc list-outside text-sm ml-4 space-y-2">
                        <li
                          v-for="indicator in linkedRisksTable.some"
                          :key="indicator.name"
                        >
                          {{ $t(getIndicatorName(indicator.name)) }}
                        </li>
                      </ul>
                      <div
                        v-if="!linkedRisksTable.some.length"
                        class="text-gray-500 text-sm"
                      >
                        {{ $t("globalInsightsPage.noIndicators") }}
                      </div>
                    </div>
                    <div class="p-4">
                      <h4
                        class="font-bold uppercase mb-2"
                        :style="{
                          color: heatmapColorScale(heatmapThresholdValues[2]),
                        }"
                      >
                        {{ $t("globalInsightsPage.highlyLinked") }}
                      </h4>
                      <ul class="list-disc list-outside text-sm ml-4 space-y-2">
                        <li
                          v-for="indicator in linkedRisksTable.high"
                          :key="indicator.name"
                        >
                          {{ $t(getIndicatorName(indicator.name)) }}
                        </li>
                      </ul>
                      <div
                        v-if="!linkedRisksTable.high.length"
                        class="text-gray-500 text-sm"
                      >
                        {{ $t("globalInsightsPage.noIndicators") }}
                      </div>
                    </div>
                  </div>

                  <div v-else class="ot-placeholder my-6 py-20">
                    {{ $t("policyPage.noData") }}
                    <!-- There is no data available for {{ indicator.name }} for
                    {{ countryName }} -->
                  </div>

                  <!-- HEAT MAP -->
                  <div class="ot-anim">
                    <h3 class="mb-2 ot-line">
                      {{ $t("globalInsightsPage.postTableText") }}
                    </h3>
                    <div class="overflow-x-scroll hide-scrollbar">
                      <HeatMap
                        :data="heatmapData"
                        class="mt-36"
                        :color-scale="heatmapColorScale"
                        :color-range="heatmapColors"
                        :categories="heatmapCategories"
                        :rotate-headers="true"
                        :rotate-right="true"
                        :selected="indicatorId.toLowerCase()"
                        row-class="cursor-pointer"
                        @select="routeHeatmap"
                        @hover="
                          $event
                            ? showHeatmapTooltip($event)
                            : hideHeatmapTooltip()
                        "
                      />
                      <!-- :highlight="highlightHeatmap"
                        :thresholds="heatmapThresholdValues" -->
                    </div>
                    <!-- LINKED RISKS LEGEND -->
                    <div
                      class="flex justify-end mt-4 text-xs items center space-x-4"
                    >
                      <div
                        @mouseenter="
                          highlightHeatmap = heatmapThresholdValues[0] - 0.1
                        "
                        @mouseleave="highlightHeatmap = null"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'square']"
                          :style="{
                            color: heatmapColorScale(
                              heatmapThresholdValues[0] - 0.1
                            ),
                          }"
                        />
                        {{ $t("globalInsightsPage.notLinked") }}
                      </div>
                      <div
                        @mouseenter="
                          highlightHeatmap = heatmapThresholdValues[1] - 0.1
                        "
                        @mouseleave="highlightHeatmap = null"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'square']"
                          :style="{
                            color: heatmapColorScale(
                              heatmapThresholdValues[1] - 0.1
                            ),
                          }"
                        />
                        {{ $t("globalInsightsPage.lowLinkage") }}
                      </div>
                      <div
                        @mouseenter="
                          highlightHeatmap = heatmapThresholdValues[2] - 0.1
                        "
                        @mouseleave="highlightHeatmap = null"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'square']"
                          :style="{
                            color: heatmapColorScale(
                              heatmapThresholdValues[2] - 0.1
                            ),
                          }"
                        />
                        {{ $t("globalInsightsPage.someLinkage") }}
                      </div>
                      <div
                        @mouseenter="highlightHeatmap = 100"
                        @mouseleave="highlightHeatmap = null"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'square']"
                          :style="{
                            color: heatmapColorScale(100),
                          }"
                        />
                        {{ $t("globalInsightsPage.highlyLinked") }}
                      </div>
                    </div>
                    <!-- LINKED RISKS TOOLTIP -->
                    <VTooltip
                      :show="heatmapTooltip.show"
                      :target="heatmapTooltip.target"
                      class="z-30"
                    >
                      <div>
                        <div class="font-bold">{{ heatmapTooltip.title }}</div>
                        <div class="font-bold mb-2">
                          {{ $t(heatmapTooltip.subtitle) }}
                        </div>
                        <div>{{ $t(heatmapTooltip.value) }}</div>
                      </div>
                    </VTooltip>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </transition>

      <!-- INDICATOR NAVIGATION -->
      <div v-if="domain" class="font-title tracking-tight">
        <section class="ot-section">
          <div class="center">
            <p class="ot-anim text-sm text-center">
              {{ $t("globalInsightsPage.explore") }}
            </p>
            <div class="grid gap-2 md:gap-6 md:grid-cols-2">
              <router-link
                v-if="indicatorNavigation[0]"
                :to="{
                  name: 'risk',
                  params: {
                    indicatorId: indicatorNavigation[0].id,
                    domainId: indicatorNavigation[0].domainId,
                    iso,
                  },
                }"
                class="ot-anim ot-card-sm group mt-4 pt-4 flex justify-between items-center shadow-md transition-shadow hover:shadow-xl focus:ring-2 focus:ring-offset-gray-100 focus:ring-primary hover:ring-2 hover:ring-offset-gray-100 hover:ring-primary"
              >
                <div>
                  <font-awesome-icon
                    :icon="['fas', 'arrow-left']"
                    class="text-gray-400 group-hover:text-primary transition-colors"
                  />
                </div>
                <div class="text-right self-start">
                  <div class="text-xs text-gray-400 mb-2">
                    {{ $t("globalInsightsPage.previous") }}
                  </div>
                  <div
                    class="group-hover:text-primary transition-colors leading-tight"
                  >
                    {{
                      $t(getIndicatorNavigationName(indicatorNavigation[0].domainId, indicatorNavigation[0].id))
                    }}
                  </div>
                  <div class="text-gray-400 text-sm">
                    {{ $t(indicatorNavigation[0].domainName) }}
                  </div>
                </div>
              </router-link>
              <div v-else></div>

              <router-link
                v-if="indicatorNavigation[1]"
                :to="{
                  name: 'risk',
                  params: {
                    indicatorId: indicatorNavigation[1].id,
                    domainId: indicatorNavigation[1].domainId,
                    iso,
                  },
                }"
                class="ot-anim ot-card-sm group mt-4 pt-4 flex justify-between items-center shadow-md transition-shadow hover:shadow-xl focus:ring-2 focus:ring-offset-gray-100 focus:ring-primary hover:ring-2 hover:ring-offset-gray-100 hover:ring-primary"
              >
                <div class="text-left self-start">
                  <div class="text-xs text-gray-400 mb-2">
                    {{ $t("globalInsightsPage.next") }}
                  </div>
                  <div
                    class="group-hover:text-primary transition-colors leading-tight"
                  >
                    {{
                      $t(getIndicatorNavigationName(indicatorNavigation[1].domainId, indicatorNavigation[1].id))
                    }}
                  </div>
                  <div class="text-gray-400 text-sm">
                    {{ $t(indicatorNavigation[1].domainName) }}
                  </div>
                </div>
                <div>
                  <font-awesome-icon
                    :icon="['fas', 'arrow-right']"
                    class="text-gray-400"
                  />
                </div>
              </router-link>
              <div v-else></div>
            </div>
          </div>
        </section>
      </div>
    </div>

    <!-- INFORMATION MODAL WINDOW -->
    <TwModal :is-open="showModal" @close="showModal = false">
      <span slot="header">Source and year</span>
      <div slot="body">
        <table class="hidden md:block min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th
                scope="col"
                class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Country
              </th>
              <th
                scope="col"
                class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Year
              </th>
              <th
                scope="col"
                class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Source
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(country, i) in data" :key="i">
              <td
                class="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900"
              >
                {{ country["Country"] }}
              </td>
              <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                {{ country["Survey Year"] }}
              </td>
              <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                {{ country["Prevalence Source"] }}
              </td>
            </tr>
          </tbody>
        </table>

        <div class="block md:hidden divide-y">
          <div v-for="(country, i) in data" :key="i" class="py-4">
            <div class="text-base font-bold text-gray-900 mb-2">
              {{ country["Country"] }}
            </div>
            <div class="text-sm text-gray-500">
              Year: {{ country["Survey Year"] }}
            </div>
            <div class="text-sm text-gray-500">
              Source: {{ country["Prevalence Source"] }}
            </div>
          </div>
        </div>
      </div>
    </TwModal>
  </div>
</template>

<script>
import api from "@/services/data";
import * as IDS from "@/utils/ids";
import * as d3 from "d3";
// import shapefile_source_variables from "@/assets/data/shapefile_source_variables";

import {
  getIndicator,
  getNumber,
  getIndicatorLow,
  getIndicatorUp,
  capitalize,
  getIndicatorNumber,
} from "@/utils/helpers";
import getTranslations from "@/utils/getTranslations"; 
import colors from "@/utils/colors";
// import { enter, leave } from "@/utils/animation";

import WorldMap from "@/components/WorldMap";
import ColumnChart from "@/components/ColumnChart";
import SearchSelect from "@/components/SearchSelect";
import HeatMap from "@/components/HeatMap";
import VTooltip from "@/components/VTooltip";
import RegionChart from "@/components/RegionChart";
import CountryMap from "@/components/CountryMap";
import DomainGrid from "@/components/DomainGrid";
import XAxis from "@/components/XAxis";
import CountryFlag from "@/components/CountryFlag";
import TwModal from "@/components/TwModal";
import LegendColor from "@/components/LegendColor";
import SelectCustom from "@/components/SelectCustom";
import DownloadButton from "@/components/DownloadButton";

export default {
  name: "RiskProfiles",
  components: {
    WorldMap,
    ColumnChart,
    SearchSelect,
    HeatMap,
    VTooltip,
    RegionChart,
    CountryMap,
    DomainGrid,
    XAxis,
    CountryFlag,
    TwModal,
    LegendColor,
    SelectCustom,
    DownloadButton,
    // DropdownSelect,
    // DomainIcon,
  },
  props: {
    // automatically injected by Vue Router because of "props: true" option in router/index.js
    domainId: {
      type: String,
    },
  },
  data() {
    return {
      publicPath: window.data_vis_uri || process.env.BASE_URL,
      loading: false,
      loadingCountry: false,
      data: [],
      linkedRisks: [],
      linkedRisksTexts: {},
      prevalenceTexts: new Map(),
      regions: [],
      error: null,
      errorCountry: null,
      heatmapColors: [colors.gradientMin, colors.gradientMax],
      grayColor: colors.gray[100],
      primaryColor: colors.primary,
      middleColor: colors.middle,
      noColorData: colors.gray[100],
      mapStrokeColor: colors.gray[200],
      columnTooltip: {
        show: false,
        value: "",
        title: "",
        target: null,
        iso: null,
      },
      worldMapTooltip: {
        show: false,
        value: "",
        title: "",
        target: null,
        iso: null,
      },
      countryMapTooltip: {
        show: false,
        value: "",
        title: "",
        target: null,
      },
      regionChartTooltip: {
        show: false,
        value: "",
        title: "",
        target: null,
      },
      heatmapTooltip: {
        show: false,
        value: "",
        title: "",
        target: null,
      },
      maxTooltip: {
        show: false,
        value: "",
        title: "",
        target: null,
      },
      flagTooltip: {
        show: false,
        value: "",
        title: "",
        target: null,
      },
      selectedRegion: null,
      highlightedCountry: null,
      showModal: false,
      fitBoundsToData: true,
      mapZoom: 1,
      currentZoom: 1,
      countryMapZoom: 1,
      countryCurrentZoom: 1,
      highlightHeatmap: null,
      maxCountryChart: null,
    };
  },
  computed: {
    dataset() {
      return this.$route.params.dataset || "girls";
    },
    iso() {
      // the order of preference for the country iso is:
      // 1 - URL param
      // 2 - localStorage value
      // 3 - Random
      return (
        this.$route.params.iso ||
        localStorage.getItem(`iso_${this.dataset}`) ||
        "CMR"
      );
    },
    selectedDomains() {
      const { domains } = getTranslations(this.$i18n);
      return domains;
    },
    indicators() {
      const indicators =
        this.selectedDomains.find((e) => e.id === this.domainId)?.indicators ||
        [];
      return indicators.map((e) => ({
        ...e,
        name: this.$t(e.short) || this.$t(e.name),
        disabled: e[`disabled_${this.dataset}`],
      }));
    },
    domain() {
      return this.selectedDomains.find((e) => e.id === this.domainId);
    },
    indicator() {
      return this.domain?.indicators.find((e) => e.id === this.indicatorId);
    },
    indicatorId() {
      // find the first indicator that is not disabled of this domain
      const indicatorId = this.domain?.indicators.find(
        (e) => !e[`disabled_${this.dataset}`]
      )?.id;
      // the one on the route, or if there's none, the first one on the list of indicators for this domain
      return this.$route.params.indicatorId || indicatorId;
    },
    dataAvailable() {
      return this.mapData[this.iso]?.value !== null || false;
    },
    countryNames() {
      const { countryNames } = getTranslations(this.$i18n);
      return countryNames;
    },
    countries() {
      return this.data
        .map((country) => ({
          id: country[IDS.ISO],
          name: this.countryNames[country[IDS.ISO]]
        }))
        .sort((a, b) => {
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;
          return 0;
        });
    },
    countryName() {
      return (
        this.countries.find((country) => country.id === this.iso)?.name || ""
      );
    },
    mapData() {
      const result = {};
      this.data.forEach((country) => {
        result[country[IDS.ISO]] = {
          value: getNumber(country[getIndicator(this.indicatorId)]),
          min: getNumber(country[getIndicatorLow(this.indicatorId)]),
          max: getNumber(country[getIndicatorUp(this.indicatorId)]),
          indicator: this.indicator.name,
          population: getNumber(country[getIndicatorNumber(this.indicatorId)]),
          total: getNumber(country[IDS.POPULATION]),
        };
      });
      return result;
    },
    columnChartData() {
      return this.data
        .map((country) => {
          const value = getNumber(country[getIndicator(this.indicatorId)]);
          return {
            id: country[IDS.ISO],
            name: country[IDS.COUNTRY_NAME],
            value,
            min: getNumber(country[getIndicatorLow(this.indicatorId)]),
            max: getNumber(country[getIndicatorUp(this.indicatorId)]),
            population: getNumber(
              country[getIndicatorNumber(this.indicatorId)]
            ),
            total: getNumber(country[IDS.POPULATION]),
            color: this.globalColorScale(value),
            // color:
            //   country[IDS.ISO] === this.iso ? colors.primary : colors.gray[200],
          };
        })
        .filter((country) => country.value !== null)
        .sort((a, b) => b.value - a.value);
    },
    heatmapData() {
      const indicators = this.linkedRisks;

      if (!indicators) return [];

      const allIndicators = this.selectedDomains.flatMap((e) => e.indicators);

      return indicators.map((indicator) => {
        const indicatorObject = allIndicators.find(
          (e) => e.id.toLowerCase() === indicator[IDS.INDICATOR_ID]
        );

        const indicatorName =
          indicatorObject?.short ||
          indicatorObject?.name ||
          indicator[IDS.INDICATOR_ID];

        return {
          name: indicatorName,
          id: indicator[IDS.INDICATOR_ID],
          // num: i + 1,
          values: indicators.map((indicator2) =>
            indicator2[IDS.INDICATOR_ID] === indicator[IDS.INDICATOR_ID]
              ? null
              : indicator[indicator2[IDS.INDICATOR_ID]] * 100
          ),
        };
      });
    },
    heatmapCategories() {
      return this.heatmapData.map((indicator) => indicator.name);
    },
    linkedRisksAvailable() {
      return this.linkedRisks.find(
        (e) => e.Indicator === this.indicatorId.toLowerCase()
      );
    },
    linkedRisksTable() {
      const risksObject =
        this.linkedRisks.find(
          (e) => e.Indicator === this.indicatorId.toLowerCase()
        ) || {};

      const scale = d3
        .scaleThreshold()
        .domain(this.heatmapThresholdValues.map((e) => e / 100))
        .range(["no", "low", "some", "high"]);

      const indicators = Object.keys(risksObject)
        .filter(
          (e) => e !== "Indicator" && e !== this.indicatorId.toLowerCase()
        )
        .map((e) => ({
          name: e,
          value: +risksObject[e],
          group: scale(+risksObject[e]),
        }));

      const groups = d3.groups(indicators, (d) => d.group);

      const result = { no: [], low: [], some: [], high: [] };

      groups.forEach(([name, values]) => (result[name] = values));

      return result;

      // return indicators;
    },
    // returns a FUNCTION that will access the variable that contains the code of the region for this country
    // based on the client's file shapefile_source_variables
    accessorForCurrentCountry() {
      // default to "REGCODE"
      // const propertyName =
      //   shapefile_source_variables[this.iso]["Shapefile variable name"] ||
      //   "REGCODE";
      // return (d) => d.properties[propertyName];

      return (d) => d.properties[IDS.REGION_FIELD_SHAPEFILES];
    },
    columnNameForCurrentCountry() {
      // default to "v024"
      // const columnName =
      //   shapefile_source_variables[this.iso]["Survey variable name"] ||
      //   IDS.DEFAULT_REGION_ID;

      const columnName = IDS.DEFAULT_REGION_ID;

      return columnName;
    },
    regionChartData() {
      return this.regions
        .map((region) => ({
          // id: `${this.iso}-${region[IDS.DEFAULT_REGION_ID]}`,
          id: `${this.iso}-${region[this.columnNameForCurrentCountry]}`,
          name: region[IDS.REGION_NAME],
          min: getNumber(region[getIndicatorLow(this.indicatorId)]),
          max: getNumber(region[getIndicatorUp(this.indicatorId)]),
          population: getNumber(region[getIndicatorNumber(this.indicatorId)]),
          total: getNumber(region[IDS.POPULATION]),
          value: getNumber(region[getIndicator(this.indicatorId)]),
        }))
        .sort((a, b) => a.value - b.value);
    },
    countryMapData() {
      const result = {};
      this.regions.forEach((region) => {
        // result[region[IDS.DEFAULT_REGION_ID]] = {
        result[region[this.columnNameForCurrentCountry]] = {
          value: getNumber(region[getIndicator(this.indicatorId)]),
          min: getNumber(region[getIndicatorLow(this.indicatorId)]),
          max: getNumber(region[getIndicatorUp(this.indicatorId)]),
          indicator: this.indicator.name,
          population: getNumber(region[getIndicatorNumber(this.indicatorId)]),
          total: getNumber(region[IDS.POPULATION]),
        };
      });
      return result;
    },
    globalMax() {
      return d3.max(this.data, (country) =>
        getNumber(country[getIndicator(this.indicatorId)])
      );
    },
    globalColorScale() {
      return d3
        .scaleSequential()
        .domain([0, this.globalMax])
        .interpolator(d3.interpolateYlGnBu)
        .unknown(this.grayColor);
    },
    countryMax() {
      return d3.max(this.regionChartData, (region) => region.value);
    },
    countryColorScale() {
      return d3
        .scaleSequential()
        .domain(this.xAxisDomainShort)
        .nice()
        .interpolator(d3.interpolateYlGnBu)
        .unknown(this.grayColor);
    },
    xAxisDomain() {
      return d3.extent(
        this.regionChartData.flatMap((e) => [
          e.min,
          this.maxCountryChart || e.max,
        ])
      );
    },
    xAxisDomainShort() {
      // without the Confidence Intervals
      return d3.extent(this.regionChartData, (d) => d.value);
    },
    heatmapDomain() {
      // return [0, 100];
      return d3.extent(
        this.heatmapData.map((row) => d3.extent(row.values)).flat()
      );
    },
    heatmapThresholdValues() {
      const divisions = 4;

      const width = this.heatmapDomain[1] - this.heatmapDomain[0];
      const step = width / divisions;

      const values = [];

      for (let i = 1; i < divisions; i++) {
        values.push(this.heatmapDomain[0] + step * i);
      }

      return values;
    },
    heatmapColorScale() {
      // return d3
      //   .scaleDiverging(
      //     [this.heatmapDomain[0], 50, this.heatmapDomain[1]],
      //     (t) => d3.interpolateRdGy(1 - t)
      //   )
      //   .unknown(this.grayColor);

      return d3.scaleThreshold(this.heatmapThresholdValues, d3.schemeRdBu[4]);
    },
    countryAverage() {
      return this.mapData[this.iso]?.value || 0;
    },
    allIndicators() {
      return this.selectedDomains
        .filter((dom) => !dom[`disabled_${this.dataset}`])
        .flatMap((dom) =>
          dom.indicators.map((e) => ({
            ...e,
            domainName: dom.name,
            domainId: dom.id,
          }))
        )
        .filter((ind) => !ind[`disabled_${this.dataset}`]);
    },
    indicatorNavigation() {
      const index = this.allIndicators.findIndex(
        (ind) => ind.id === this.indicatorId
      );
      const prev = this.allIndicators[index - 1];
      const next = this.allIndicators[index + 1];
      return [prev, next];
    },
    linkedRisksText() {
      return this.linkedRisksTexts[this.iso];
    },
    prevalenceText() {
      const { prevalenceColumn } = getTranslations(this.$i18n);
      return this.prevalenceTexts?.get(this.indicatorId)?.[0][prevalenceColumn];
    },
    countryDataLink() {
      return `/data/${this.dataset}/prevalence/${this.iso}_subnat_prev_data.csv`;
    },
    linkedRisksDataLink() {
      return `/data/${this.dataset}/linkedrisks/${this.iso}_LinkedRisks.csv`;
    },
    globalDataLink() {
      return `/data/${this.dataset}/prevalence/all_nat_prev_data.csv`;
    },
    domainsOptions() {
      return [
        { name: `-- ${this.$t("select-domain").toUpperCase()} --`, id: "" },
        ...this.selectedDomains.map((e) => {
          return {
            ...e,
            name: this.$t(e.name),
            text: this.$t(e.text),
            disabled: e[`disabled_${this.dataset}`],
          };
        }),
      ];
    },
  },
  mounted() {
    localStorage.setItem(`iso_${this.dataset}`, this.iso);
    this.loadAll();
  },
  beforeDestroy() {
    this.final();
  },
  watch: {
    iso() {
      // won't be triggered if we go back to homepage because then ISO will default to the localStorage,
      // therefore it won't change (see iso computed prop above in this file)
      localStorage.setItem(`iso_${this.dataset}`, this.iso);
      this.loadCountryData();
      this.loadPrevalenceTexts();
    },
    dataset() {
      this.loadAll();
    },
  },
  methods: {
    loadAll() {
      this.loadData();
      this.loadCountryData();
      this.loadLinkedRisksTexts();
      this.loadPrevalenceTexts();
      this.initial();
    },
    async loadData() {
      try {
        this.loading = true;
        this.data = await api.getGlobalData(this.dataset);
      } catch (err) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    async loadCountryData() {
      try {
        this.loadingCountry = true;
        const [linkedRisks, regions] = await Promise.all([
          api.getLinkedRisks(this.iso, this.dataset),
          api.getPrevalence(this.iso, this.dataset),
        ]);
        this.linkedRisks = linkedRisks || [];
        this.regions = regions || [];
      } catch (err) {
        this.errorCountry = true;
      } finally {
        this.loadingCountry = false;
      }
    },
    async loadLinkedRisksTexts() {
      try {
        // this.loading = true;

        this.linkedRisksTexts = await api.getLinkedRisksTexts(this.dataset);
      } catch (err) {
        this.error = true;
      } finally {
        // this.loading = false;
      }
    },
    async loadPrevalenceTexts() {
      try {
        // this.loading = true;

        this.prevalenceTexts = await api.getPrevalenceTexts(
          this.iso,
          this.dataset,
        );
      } catch (err) {
        this.error = true;
      } finally {
        // this.loading = false;
      }
    },
    routeMap({ iso }) {
      if (this.mapData[iso]) this.routeCountry(iso);
    },
    routeCountry(event) {
      this.route({ iso: event });
    },
    routeIndicator(indicatorId) {
      this.route({ indicatorId });
    },
    routeIndicatorSelect(indicator) {
      this.route({ indicatorId: indicator.id.toUpperCase() });
    },
    routeHeatmap(indicator) {
      const domain = this.selectedDomains.find(
        (e) =>
          e.indicators.findIndex(
            (ind) => ind.id === indicator.id.toUpperCase()
          ) >= 0
      );

      this.route({
        domainId: domain.id,
        indicatorId: indicator.id.toUpperCase(),
      });
    },
    routeDomain(domainId) {
      // const domainId = event.target.value;
      // find the first indicator of this domain
      const indicatorId = this.selectedDomains.find((e) => e.id === domainId)
        ?.indicators[0]?.id;

      // if back to homepage, remove the ISO from the URL
      if (!domainId) this.route({ domainId, indicatorId, iso: "" });
      // reset the indicatorId when changing the domain
      else this.route({ domainId, indicatorId, iso: this.iso });
    },
    route(params) {
      const query = { ...this.$route.params, ...params };

      for (const key in query) {
        if (Object.hasOwnProperty.call(query, key)) {
          if (!query[key]) delete query[key];
        }
      }

      this.$router.push({
        name: "risk",
        params: query,
        // query: this.$route.query,
      });
    },
    showColumnTooltip({ data, event }) {
      this.columnTooltip.target = event.target;
      this.columnTooltip.value = data.value;
      this.columnTooltip.min = data.min;
      this.columnTooltip.max = data.max;
      this.columnTooltip.title = data.name;
      this.columnTooltip.number = data.population;
      this.columnTooltip.total = data.total;
      this.columnTooltip.subtitle = this.indicator?.name;
      this.columnTooltip.iso = data.id;
      this.columnTooltip.show = true;

      // set highlight for the world map
      this.highlightedCountry = data.id;
    },
    hideColumnTooltip() {
      this.columnTooltip.show = false;

      // set highlight for the world map
      this.highlightedCountry = null;
    },
    showWorldMapTooltip({ data, event }) {
      this.worldMapTooltip.target = event.target;
      this.worldMapTooltip.value = data.value;
      this.worldMapTooltip.min = data.min;
      this.worldMapTooltip.max = data.max;
      this.worldMapTooltip.title = data.name;
      this.worldMapTooltip.number = data.population;
      this.worldMapTooltip.total = data.total;
      this.worldMapTooltip.subtitle = this.indicator?.name;
      this.worldMapTooltip.iso = data.id;
      this.worldMapTooltip.show = true;
    },
    hideWorldMapTooltip() {
      this.worldMapTooltip.show = false;
    },
    highlightWorldMap(event) {
      if (!event) {
        this.highlightedCountry = null;

        // hide flag on column chart
        this.showFlagTooltip(null);
        this.hideWorldMapTooltip();
      } else {
        const { data } = event;

        const iso = data.id;
        // if country does not exist in our data, do not highlight it
        // or if the data is null
        if (!this.mapData[iso] || this.mapData[iso].value === null)
          return (this.highlightedCountry = null);
        // otherwise, highlight it
        this.highlightedCountry = iso;

        data.name = this.countryNames[iso];

        this.showWorldMapTooltip({ data, event });

        // show flag on column chart
        this.showFlagTooltip({ iso });
      }
    },
    showFlagTooltip(data) {
      if (!data) this.flagTooltip.show = false;
      else {
        const { iso } = data;
        this.flagTooltip.target = document.getElementById(`column-${iso}`);

        this.flagTooltip.iso = iso;
        this.flagTooltip.show = true;
      }
    },
    showCountryMapTooltip({ data }) {
      // set the selected region
      this.selectedRegion = data.id;

      // and the contents of the tooltip
      this.countryMapTooltip.name = capitalize(data.name);
      this.countryMapTooltip.value = data.value;
      this.countryMapTooltip.min = data.min;
      this.countryMapTooltip.max = data.max;
      this.countryMapTooltip.number = data.population;
      this.countryMapTooltip.total = data.total;
      this.countryMapTooltip.subtitle = this.indicator?.name;
      this.countryMapTooltip.show = true;
    },
    hideCountryMapTooltip() {
      this.selectedRegion = null;
      this.countryMapTooltip.show = false;
    },
    setSelectedRegion(data) {
      if (data?.data.id) {
        const [, id] = data?.data.id.split("-");
        this.selectedRegion = id;

        // show tooltip
        this.regionChartTooltip.target = data.event.target;
        this.regionChartTooltip.name = capitalize(data.data.name);
        this.regionChartTooltip.value = data.data.value;
        this.regionChartTooltip.min = data.data.min;
        this.regionChartTooltip.max = data.data.max;
        this.regionChartTooltip.number = data.data.population;
        this.regionChartTooltip.total = data.data.total;
        this.regionChartTooltip.subtitle = this.indicator?.name;
        this.regionChartTooltip.show = true;
      } else {
        this.selectedRegion = null;

        // hide tooltip
        this.regionChartTooltip.show = false;
      }
    },
    showHeatmapTooltip({ event, data, i }) {
      this.heatmapTooltip.target = event.target;

      const value = data.values[i];
      let text;

      if (value < this.heatmapThresholdValues[0])
        text = this.$t("globalInsightsPage.notLinked");
      else if (value < this.heatmapThresholdValues[1])
        text = this.$t("globalInsightsPage.lowLinkage");
      else if (value < this.heatmapThresholdValues[2])
        text = this.$t("globalInsightsPage.someLinkage");
      else text = this.$t("globalInsightsPage.highlyLinked");

      const vs = this.heatmapData[i].name;
      this.heatmapTooltip.value = text;
      this.heatmapTooltip.title = data.name;
      this.heatmapTooltip.subtitle = vs;

      if (data.name !== vs) this.heatmapTooltip.show = true;
      else this.heatmapTooltip.show = false;
    },
    hideHeatmapTooltip() {
      this.heatmapTooltip.show = false;
    },
    showMaxTooltip({ target }) {
      this.maxTooltip.target = target;
      this.maxTooltip.show = true;
    },
    hideMaxTooltip() {
      this.maxTooltip.show = false;
    },
    setCurrentZoom(transform) {
      this.currentZoom = transform.k;
    },
    increaseZoom() {
      this.mapZoom++; // = parseInt(this.currentZoom) + 1
    },
    setCurrentCountryZoom(transform) {
      this.countryCurrentZoom = transform.k;
    },
    increaseCountryZoom() {
      this.countryMapZoom++; // = parseInt(this.currentZoom) + 1
    },
    zoomMap() {
      this.$refs.worldMap.zoomToFitBounds();
    },
    getIndicatorName(id) {
      const allIndicators = this.selectedDomains.flatMap((e) => e.indicators);

      return allIndicators.find((e) => e.id.toLowerCase() === id)?.name || id;
    },
    getIndicatorNavigationName(domainId, indicatorId) {
      const domain = this.selectedDomains.find(dom => dom.id === domainId);
      if (!domain) return undefined;

      const indicator = domain.indicators.find(ind => ind.id === indicatorId);
      if (!indicator) return undefined;

      return indicator.short || indicator.name;
    },
    initial() {},
    final() {},
  },
};
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease-in-out;
  opacity: 1;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fade-delay-enter-active,
.fade-delay-leave-active {
  transition: opacity 0.25s 0.5s ease-in-out;
  opacity: 1;
}
.fade-delay-enter,
.fade-delay-leave-to {
  opacity: 0;
}
</style>
